<template>
    <transition name="slide-in-right">
        <div ref="sidebarRef" class="sidebar-container">
            <!-- Header with title and close button inline -->
            <div v-if="company" class="sidebar-header">
                <div class="company-header-content">
                    <PrimeAvatar :image="company.logo" size="md" aria-label="" />
                    <div class="company-info">
                        <div class="company-name-row">
                            <div class="company-name">
                                <span ref="companyNameSpan" @mouseenter="companyNameShow" @mouseleave="companyNameHide">{{
                                    company.name
                                }}</span>
                                <a :href="company.url.startsWith('http') ? company.url : `https://${company.url}`" target="_blank" rel="noopener noreferrer" class="external-link">
                                    View Website
                                    <PhArrowSquareOut :size="12" />
                                </a>
                            </div>

                            <PrimeBadge
                                @mouseenter="guideShow"
                                @mouseleave="guideHide"
                                class="whitespace-nowrap cursor-pointer"
                                v-if="company.privacy_policy"
                            >
                                <PhCheck :size="12" />
                                <span>Privacy Policy Verified</span>
                                <!-- The hover popover carries this explanation for mouse users only; the badge
                                     is not focusable, so the same copy has to live in the badge's own text. -->
                                <span class="sr-only">
                                    This privacy policy has been reviewed and confirmed to meet our standards for transparency and
                                    data handling.
                                </span>
                            </PrimeBadge>
                        </div>
                    </div>
                    <PrimePopover
                        ref="popoverCompanyName"
                        class="company-name-popover"
                        :pt="{
                            content: {
                                class: 'popover-content',
                            },
                        }"
                    >
                        <div class="company-tooltip-content">
                            <div class="company-item">
                                <p class="company-description">{{ company?.name }}</p>
                            </div>
                        </div>
                    </PrimePopover>
                    <PrimePopover
                        ref="popoverGuide"
                        class="guide-popover"
                        :pt="{
                            content: {
                                class: 'popover-content',
                            },
                        }"
                    >
                        <div class="company-tooltip-content">
                            <div class="company-item">
                                <p class="company-description">
                                    This privacy policy has been<br /> reviewed and confirmed to <br />  meet our standards for<br /> transparency and
                                    data handling.
                                </p>
                            </div>
                        </div>
                    </PrimePopover>
                </div>
                <button type="button" autofocus aria-label="Close exposure details" @click="closeDialog" class="p-2 rounded">
                    <XMarkIcon class="h-6 w-6" />
                </button>
            </div>
            <div v-else class="sidebar-header">
                <div class="company-header-skeleton">
                    <div class="skeleton-avatar"></div>
                    <div class="skeleton-text">
                        <div class="skeleton-line"></div>
                    </div>
                </div>
                <button type="button" aria-label="Close exposure details" @click="closeDialog" class="p-2 rounded hover:bg-gray-200">
                    <XMarkIcon class="h-6 w-6" />
                </button>
            </div>

            <!-- Status Display (Stepper) -->
            <div class="stepper-container">
                <TimelineStatus v-if="exposure" :exposure="exposure" />
                <!-- The fetch failed, so nothing below will ever fill in. Say so rather than
                     leaving the member watching skeletons. -->
                <p v-else-if="exposureError" class="exposure-error" role="alert">{{ exposureError }}</p>
                <!-- Company-only mode: there is no exposure to track, so offer the action that
                     starts one instead of a status bar that would never fill in. -->
                <div v-else-if="isCompanyOnly && company" class="company-only-action">
                    <p>We haven't tracked any exposures from this company, but it's in our database.</p>
                    <PrimeButton class="shrink-0 whitespace-nowrap" label="Request Data Deletion" @click="requestDataDeletion" />
                </div>
                <div v-else class="stepper-skeleton">
                    <div class="skeleton-bar"></div>
                </div>
            </div>

            <!-- Re-Exposures Section -->
            <ReExposuresSection v-if="exposure && hasActiveLicense" />

            <!-- Tabs Section -->
            <div v-if="company" class="tabs-section">
                <PrimeTabs :value="activeTab" @update:value="(val: string | number) => { activeTab = String(val); }" class="w-full">
                    <div class="tabs-header">
                        <PrimeTabList variant="tertiary">
                            <PrimeTab variant="tertiary" value="activity">Activity</PrimeTab>
                            <PrimeTab variant="tertiary" value="deletion-guide">Deletion Guide</PrimeTab>
                            <PrimeTab variant="tertiary" value="advisories">Advisories</PrimeTab>
                        </PrimeTabList>
                    </div>
                    <PrimeTabPanels>
                        <PrimeTabPanel value="activity">
                            <ExposureActivityTab />
                        </PrimeTabPanel>
                        <PrimeTabPanel value="deletion-guide">
                            <ExposureDeletionGuideTab />
                        </PrimeTabPanel>
                        <PrimeTabPanel value="advisories">
                            <ExposureAdvisoriesTab />
                        </PrimeTabPanel>
                    </PrimeTabPanels>
                </PrimeTabs>
            </div>
            <div v-else class="tabs-skeleton">
                <div class="skeleton-tabs"></div>
                <div class="skeleton-content">
                    <div class="skeleton-item"></div>
                    <div class="skeleton-item"></div>
                </div>
            </div>
        </div>
    </transition>
</template>
<script setup lang="ts">
import PrimePopover from 'primevue/popover';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { PhArrowSquareOut } from '@phosphor-icons/vue';
import { getDialogRef, useDynamicDialog } from '~/composables/dynamic-dialog';
import { getDeletionRequestGate } from './deletionEligibility';
import { useExposureSidebarStore } from '~/stores/components/modals/exposure-sidebar';
import { usePersonStore } from '~/stores/person';
import { ModalNames } from '~/types/modals';
import { urlWithoutDrawerQuery } from '~/utils/drawer-history';
import ExposureActivityTab from './ExposureTabs/ExposureActivityTab.vue';
import ExposureAdvisoriesTab from './ExposureTabs/ExposureAdvisoriesTab.vue';
import ExposureDeletionGuideTab from './ExposureTabs/ExposureDeletionGuideTab.vue';
import ReExposuresSection from './ReExposuresSection.vue';
import TimelineStatus from './CompanySideBar/TimelineStatus.vue';

const dialogRef = getDialogRef(ModalNames.EXPOSURE_SIDEBAR);
const sidebarStore = useExposureSidebarStore();
const personStore = usePersonStore();
const { hasActiveLicense, gate, promptForGate } = useLicenseAccess();

const { exposure, exposureError, company } = storeToRefs(sidebarStore);
const activeTab = ref('activity'); // Activity tab selected by default

// Opened for a company rather than an exposure — decided at open time, so it doesn't flicker
// while an exposure is still being fetched.
const isCompanyOnly = computed(
    () => Boolean(sidebarStore.selectedCompanyId) && !sidebarStore.selectedExposure
);

const requestDataDeletion = () => {
    if (!company.value) return;

    // Check eligibility before the confirm dialog, not after: the modal calls requestDeletion
    // directly, so an ineligible member would otherwise confirm and land on a backend rejection
    // with no route to the license they actually need.
    //
    // A lapsed seat is no seat, so the licence gate answers first; the plan-level rule below only
    // runs for a member who is actually protected.
    if (!hasActiveLicense.value) {
        promptForGate(gate.value);
        return;
    }

    const deletionGate = getDeletionRequestGate({
        licenseSlug: personStore.person?.license?.slug,
        companyType: company.value.type?.value,
    });

    if (deletionGate !== 'allowed') {
        promptForGate('needs_upgrade');
        return;
    }

    useDynamicDialog(
        ModalNames.DELETION_REQUEST,
        {
            company: {
                id: company.value.id,
                name: company.value.name,
                type: company.value.type?.label ?? '',
                logo: company.value.logo,
                url: company.value.url,
                slug: company.value.slug,
            },
        },
        {
            // The request is filed and the company is now monitored, so this drawer's
            // company-only view is stale — and its button would happily file a duplicate.
            onClose: (options) => {
                if (options?.data?.success) closeDialog();
            },
        }
    );
};
const sidebarRef = ref<HTMLElement | null>(null);
const companyNameSpan = ref<HTMLElement>();
const isCompanyNameTruncated = ref(false);
const popoverCompanyName = ref();
const popoverGuide = ref();

// Reset to activity tab when exposure changes
watch(
    exposure,
    () => {
        if (exposure.value) {
            activeTab.value = 'activity';
        }
    },
    { immediate: true }
);

const closeDialog = () => {
    dialogRef.value.close();
    if (window.history.state?.modalOpen) {
        window.history.back();
    }
};

// Handle clicks outside the sidebar to close it
const handleClickOutside = (event: MouseEvent) => {
    if (!sidebarRef.value) return;

    const target = event.target as HTMLElement;

    // Check if click is outside the sidebar container
    if (!sidebarRef.value.contains(target)) {
        // Check if click is directly on the dialog mask/backdrop
        // PrimeVue uses .p-dialog-mask, and we also use .modal-main class from the composable
        const isMaskClick = target.classList.contains('p-dialog-mask') || target.classList.contains('modal-main');

        if (isMaskClick) {
            closeDialog();
        }
    }
};

onMounted(() => {
    window.addEventListener('popstate', handleBackNavigation);
    // Add click listener to document to detect clicks outside
    document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
    window.removeEventListener('popstate', handleBackNavigation);
    document.removeEventListener('mousedown', handleClickOutside);
});

const handleBackNavigation = () => {
    dialogRef.value.close();
    history.replaceState(null, '', urlWithoutDrawerQuery());
};

const checkNameTruncation = () => {
    if (!companyNameSpan.value) {
        isCompanyNameTruncated.value = false;
        return;
    }
    isCompanyNameTruncated.value = companyNameSpan.value.scrollWidth > companyNameSpan.value.clientWidth;
};

watch(
    () => company.value?.name,
    () => {
        nextTick(() => {
            checkNameTruncation();
            // Hide popover if name is no longer truncated
            if (!isCompanyNameTruncated.value && popoverCompanyName.value?.visible) {
                popoverCompanyName.value.hide();
            }
        });
    },
    { immediate: true }
);

const companyNameShow = (event: Event) => {
    if (isCompanyNameTruncated.value) {
        popoverCompanyName.value.show(event, companyNameSpan.value);
    }
};

const companyNameHide = (event: Event) => {
    if (isCompanyNameTruncated.value) {
        popoverCompanyName.value.hide();
    }
};

const guideShow = (event: Event) => {
    popoverGuide.value.show(event);
};

const guideHide = (event: Event) => {
    popoverGuide.value.hide();
};
</script>

<style lang="scss" scoped>
.sidebar-container {
    @apply bg-white sm:w-[532px] w-full h-screen flex flex-col overflow-y-auto right-0 fixed top-0;
    animation: slide-in-right 0.3s ease-in-out;

    .sidebar-header {
        @apply flex justify-between items-center w-full pl-6 pr-4 py-4;

        .company-header-content {
            @apply flex items-center gap-3 flex-1;

            .company-info {
                @apply flex-1;

                .company-name-row {
                    @apply flex items-center gap-2 justify-between w-full;

                    .company-name {
                        @apply flex flex-col gap-1 items-start;

                        span {
                            @apply text-zinc-900 text-lg font-semibold whitespace-nowrap max-w-[200px] truncate;
                        }
                    }

                    .external-link {
                        @apply flex gap-1 items-center text-blue-600 hover:underline text-xs font-semibold leading-none;
                    }
                }
            }
        }

        .company-header-skeleton {
            @apply flex items-center gap-3 flex-1;

            .skeleton-avatar {
                @apply w-10 h-10 bg-gray-200 rounded-full animate-pulse;
            }

            .skeleton-text {
                @apply flex-1;

                .skeleton-line {
                    @apply h-6 bg-gray-200 rounded animate-pulse;
                    width: 200px;
                }
            }
        }
    }
}

.tabs-section {
    @apply flex-1 flex flex-col min-h-0;

    .tabs-header {
        @apply flex items-start self-stretch border-y border-b-0 border-zinc-300 p-6 w-full relative;
        @apply bg-[url('@/assets/images/png/main/person/company-sidebar/tabs-bg.png')] bg-center bg-cover bg-no-repeat;
    }
}

.tabs-skeleton {
    @apply flex-1 flex flex-col;

    .skeleton-tabs {
        @apply h-16 bg-gray-200 animate-pulse border-b border-zinc-200;
    }

    .skeleton-content {
        @apply flex-1 p-6 space-y-4;

        .skeleton-item {
            @apply h-20 bg-gray-200 rounded animate-pulse;
        }
    }
}
.circle-1 {
    @apply absolute border h-8 w-8 border-amber-400 rounded-full top-[-18%] left-[-18%];
    animation: circle-animation 4s infinite ease-in-out;
}

.circle-2 {
    @apply absolute border h-8 w-8 border-amber-400 rounded-full top-[-18%] left-[-18%];
    animation: circle-animation 6s infinite ease-in-out;
    animation-delay: 1s;
}

@keyframes circle-animation {
    0% {
        border: solid 1px #fff;
        transform: scale(0);
    }

    50% {
        border: solid 1px var(--brand-rail);
        transform: scale(1);
    }

    100% {
        border: solid 1px #fff;
        transform: scale(0);
    }
}

//using vue3 transition to slide in from right

@keyframes slide-in-right {
    from {
        transform: translateX(100%);
    }

    to {
        transform: translateX(0);
    }
}

@keyframes slide-out-right {
    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(100%);
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.exposure-error {
    @apply w-full px-6 py-3 text-sm font-medium text-red-600 bg-zinc-50 border-t border-zinc-300;
}

.company-only-action {
    @apply flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-4 px-6 bg-zinc-50 border-t border-b border-zinc-200;

    p {
        @apply text-zinc-600 text-sm font-normal leading-5;
    }
}

// Skeleton loading styles
.stepper-skeleton {
    @apply flex justify-center items-center gap-3 py-2 px-6 bg-zinc-50 border-t border-b border-zinc-200;

    .skeleton-bar {
        @apply h-4 bg-gray-200 rounded animate-pulse;
        width: 200px;
    }
}

.specific-exposure-skeleton {
    @apply p-6 border-b border-zinc-200;

    .skeleton-header {
        @apply h-6 bg-gray-200 rounded animate-pulse mb-4;
        width: 150px;
    }

    .skeleton-content {
        @apply space-y-3;

        .skeleton-line {
            @apply h-4 bg-gray-200 rounded animate-pulse;
            width: 100%;
        }
    }
}

.activity-feed-skeleton {
    @apply p-6;

    .skeleton-header {
        @apply h-6 bg-gray-200 rounded animate-pulse mb-4 w-32;
    }

    .skeleton-content {
        @apply space-y-4;

        .skeleton-item {
            @apply h-16 bg-gray-200 rounded animate-pulse;
            width: 100%;
        }
    }
}

.company-tooltip-content {
    @apply flex flex-col items-start rounded-lg bg-tooltip p-3 gap-2;
    min-width: 170px;
}

.company-item {
    @apply flex flex-col;
}

.company-description {
    @apply text-zinc-200 text-xs font-medium leading-5;
}

:global(.company-name-popover[data-pc-section='root']) {
    border-radius: 8px !important;
}

// Hide tooltip preset arrows
:deep(.p-tooltip--dark.p-tooltip--top)::after {
    display: none !important;
}

.company-name-popover::after,
.company-name-popover[data-pc-section='root']::after,
.company-name-popover :deep([data-pc-section='arrow']),
.company-name-popover :deep(.p-popover-arrow) {
    display: none !important;
}

:global(.company-name-popover[data-pc-section='root']::before) {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    left: 50%;
    top: -8px;
    transform: translateX(-50%);
    border-style: solid;
    border-width: 0 8px 8px 8px;
    border-color: transparent transparent var(--tooltip-bg) transparent;
}
[role='tooltip'].p-tooltip--dark.p-tooltip--top::after,
.p-tooltip--dark.p-tooltip--top::after,
[role='tooltip'].p-tooltip--dark.p-tooltip--top::after,
.p-tooltip--dark.p-tooltip--top::after {
    display: none !important ;
}

:global(.guide-popover[data-pc-section='root']) {
    border-radius: 8px !important;
}

// Hide tooltip preset arrows
:deep(.p-tooltip--dark.p-tooltip--top)::after {
    display: none !important;
}

.guide-popover::after,
.guide-popover[data-pc-section='root']::after,
.guide-popover :deep([data-pc-section='arrow']),
.guide-popover :deep(.p-popover-arrow) {
    display: none !important;
}

:global(.guide-popover[data-pc-section='root']::before) {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    left: 50%;
    top: -8px;
    transform: translateX(-50%);
    border-style: solid;
    border-width: 0 8px 8px 8px;
    border-color: transparent transparent var(--tooltip-bg) transparent;
}
</style>
